import { DefaultQueryParams } from '@frontend/api-utils';
import { ObjectBadgeMapper } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { useListRepository } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { LuX } from 'react-icons/lu';

import FilterModal from './filter-modal/filter-modal.component';
import useFilter from './filter.controller';
import { SearchFilter } from './search-filter';

const ID = 'filter';

export interface FilterProps<T extends object & { id: string }> extends ReturnType<typeof useListRepository<T>> {
    dispatch: ThunkDispatch<any, any, Action>;
    filter?: FilterOptions;
}

export interface FilterOptions {
    search?: boolean;
    searchPlaceholder?: string;

    keys?: (DefaultQueryParams | string | number)[];
    displayMap?: Map<string, React.ReactNode>;
    keyProps?: Map<string, any>;
    queryParamKeyOverwrite?: Map<string, string>;
    defaultValues?: Map<string, any>;
}

const Filter = <T extends object & { id: string }>(props: FilterProps<T>) => {
    const viewProps = useFilter<T>(props);

    return (
        <div className='flex w-full flex-col gap-1'>
            <div
                id={ID}
                className='inline-flex w-full items-start justify-start gap-2 self-stretch'>
                {props.filter?.search && (
                    <SearchFilter
                        placeholder={props.filter.searchPlaceholder ?? undefined}
                        listId={props.listId}
                        dispatch={props.dispatch}
                    />
                )}

                {props.filter?.keys && (
                    <div
                        onClick={() => viewProps.setShowFilters(true)}
                        className='outline-border-default bg-background-surface inline-flex h-11 flex-col items-start justify-center gap-3.5 rounded-xl px-4 py-3 outline-1 outline-offset-[-1px]'>
                        <div className='text-foreground-default justify-start text-center text-base font-medium'>{CommonMessage.BUTTONS.FILTER}</div>
                    </div>
                )}

                {viewProps.showFilters && (
                    <FilterModal<T>
                        handleClose={() => viewProps.setShowFilters(false)}
                        {...props}
                    />
                )}
            </div>

            <div className='flex flex-wrap gap-2'>
                {viewProps.filterValues.map((filter) => {
                    if (!filter.value || filter.value.length === 0) return null;
                    if (props.filter?.defaultValues && Array.from(props.filter?.defaultValues).find(([key]) => key === filter.key)) return null;
                    return filter.value.map((v) => {
                        return (
                            <div className='size- bg-background-surface inline-flex items-center justify-start gap-2 rounded-full px-3 py-2 outline-1 outline-offset-[-1px] outline-neutral-600'>
                                <div className='text-foreground-default justify-start text-center text-sm font-semibold'>
                                    {filter.key.endsWith('_id') ? (
                                        <ObjectBadgeMapper
                                            type={filter.key}
                                            id={v}
                                            className='text-foreground-default justify-start text-center text-sm font-semibold'
                                        />
                                    ) : (
                                        <span>{v.replace(/_/g, ' ')}</span>
                                    )}
                                </div>
                                <LuX onClick={() => viewProps.removeFilter(filter.key, v)} />
                            </div>
                        );
                    });
                })}
            </div>
        </div>
    );
};

export default Filter;
